// Routes excluded from visual refresh theme (blocklist approach)
const EXCLUDED_ROUTES = [
    /^\/premier-agent(\/.*)?$/, // Matches /premier-agent/ and all sub-paths
];

/**
 * Safely parses pathname from asPath with error handling
 */
export function parsePathname(asPath) {
    try {
        return new URL(asPath, 'http://dummy.base').pathname;
    } catch (error) {
        return asPath || '/';
    }
}

const B2B_PATH_PREFIXES = [
    '/premier-agent',
    '/premieragent',
    '/new-construction-advertising',
    '/rentals-network',
    '/agents',
    '/pro',
    '/preferred',
    '/news',
    '/research',
];

// Matches whole path segments so /pro doesn't match /product-releases/
function matchesPathPrefix(asPath, prefixes) {
    const p = parsePathname(asPath)?.toLowerCase() || '';
    return prefixes.some(
        (prefix) => p === prefix || p.startsWith(`${prefix}/`)
    );
}

export function isB2BPath(asPath) {
    return matchesPathPrefix(asPath, B2B_PATH_PREFIXES);
}

export function shouldUseVisualRefreshTheme(pathname, query = {}) {
    // Normalize pathname once
    const p = pathname?.toLowerCase() || '';

    // Special case for preview - only use visual refresh if explicitly requested via query param
    if (p.includes('/preview/') && query?.useVisualRefresh !== undefined) {
        const useVisualRefreshParam = Array.isArray(query.useVisualRefresh)
            ? query.useVisualRefresh[0]
            : query.useVisualRefresh;
        // Handle both boolean and string values
        return (
            useVisualRefreshParam === true || useVisualRefreshParam === 'true'
        );
    }

    // Return true for all routes EXCEPT those in the blocklist
    return !EXCLUDED_ROUTES.some((regex) => regex.test(p));
}

// Sites rendered with the `zpro` theme (Zillow Pro, MOPS-31773). Both
// prefixes serve the AGENTS WordPress site (`/agents/*` redirects to `/pro/*`),
// including their `/preview/` routes.
const ZPRO_THEME_PATH_PREFIXES = ['/pro', '/agents'];

export function isZproThemePath(asPath) {
    return matchesPathPrefix(asPath, ZPRO_THEME_PATH_PREFIXES);
}

/**
 * Panda theme (`data-panda-theme`) for a page. `zpro` builds on the Visual
 * Refresh `zillow` theme, so it only applies when visual refresh is on. With
 * it off (e.g. a preview with `?useVisualRefresh=false`) every path gets
 * `legacy-zillow`, matching the styled-components theme and page frame.
 *
 * @param {string} pathname
 * @param {boolean} useVisualRefreshTheme result of shouldUseVisualRefreshTheme
 * @returns {'zpro' | 'zillow' | 'legacy-zillow'}
 */
export function getThemeName(pathname, useVisualRefreshTheme) {
    if (!useVisualRefreshTheme) {
        return 'legacy-zillow';
    }
    return isZproThemePath(pathname) ? 'zpro' : 'zillow';
}

const _zconfigs = {
    prod: {
        serverURLs: {
            MAX: 'https://mortgageapi.zillow.com',
        },
        partnerId: 'RD-WXDQSGN',
    },
    default: {
        serverURLs: {
            MAX: 'https://mortgageapi.qa.zillow.net',
        },
        partnerId: 'RD-GKZRBBL',
    },
};

export function getZmAPIConfig() {
    return _zconfigs[process.env.NEXT_PUBLIC_ENV] || _zconfigs.default;
}
